import { ChangeDetectionStrategy, Component, computed, inject, input, model } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { TextInput } from '../../../shared/components/forms/text-input';
import { NumberInput } from '../../../shared/components/forms/number-input';
import { Select, SelectOption } from '../../../shared/components/forms/select';
import { Toggle } from '../../../shared/components/forms/toggle';
import { LanguageService } from '../../../core/i18n/language.service';
import { GtfsFieldComponent } from './gtfs-field.component';
import {
  GTFS_DEFAULTS,
  GTFS_LINE_NAME_SOURCES,
  GTFS_MINIMUMS,
  GtfsConfigForm,
  GtfsFormField,
  gtfsCleardownEnabled,
  gtfsFormErrors,
  gtfsRealtimeEnabled,
} from '../util/gtfs-config';

export type GtfsConfigPane = 'realtime' | 'departures' | 'advanced';

/**
 * One configuration tab pane of the GTFS feed page — Realtime, Departures
 * board or Advanced — over the page's single `GtfsConfigForm` signal, so
 * switching tabs never loses edits. The trip-updates URL is the realtime
 * master switch: the alerts/vehicle-positions URLs and the cleardown toggle
 * stay disabled until it's set. Mapping to/from the opaque configuration map,
 * and validation, live in `util/gtfs-config.ts`.
 */
@Component({
  selector: 'app-gtfs-config',
  standalone: true,
  imports: [FormsModule, TranslocoDirective, TextInput, NumberInput, Select, Toggle, GtfsFieldComponent],
  templateUrl: './gtfs-config.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class GtfsConfigComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly languageService = inject(LanguageService);

  readonly pane = input.required<GtfsConfigPane>();
  readonly value = model.required<GtfsConfigForm>();

  readonly defaults = GTFS_DEFAULTS;
  readonly minimums = GTFS_MINIMUMS;
  readonly realtime = computed(() => gtfsRealtimeEnabled(this.value()));
  readonly cleardownEnabled = computed(() => gtfsCleardownEnabled(this.value()));
  readonly errors = computed(() => gtfsFormErrors(this.value()));

  readonly lineNameOptions = computed<SelectOption[]>(() => {
    this.languageService.current(); // re-evaluate on language switch
    return GTFS_LINE_NAME_SOURCES.map((source) => ({
      value: source,
      label: this.transloco.translate(`providers.feedPage.gtfs.lineNameSource.${source}`),
    }));
  });

  patch(changes: Partial<GtfsConfigForm>): void {
    this.value.update((v) => ({ ...v, ...changes }));
  }

  /** Picking a supported source clears the unrecognised stored one. */
  setLineNameSource(source: GtfsConfigForm['lineNameSource']): void {
    this.patch({ lineNameSource: source, unrecognisedLineNameSource: source ? null : this.value().unrecognisedLineNameSource });
  }

  /** Translated error for a numeric/URL field, or null. */
  errorText(field: GtfsFormField, min?: number): string | null {
    const error = this.errors()[field];
    return error ? this.transloco.translate(`providers.feedPage.gtfs.errors.${error}`, { min }) : null;
  }
}
